/**
 * Google Consent Mode v2 helpers. Sets safe defaults BEFORE GA4 / Google Ads
 * load so that signals are gated on user consent in regions that require it
 * (EEA, UK, Switzerland) and behaves normally elsewhere.
 *
 * Storage:
 *  - localStorage key: `fml_consent_v1` -> JSON {analytics: bool, ads: bool, ts}
 */

const CONSENT_KEY = 'fml_consent_v1';

const isBrowser = () => typeof window !== 'undefined' && typeof document !== 'undefined';

const isPrerender = () =>
  isBrowser() &&
  (window.__PRERENDER_INJECTED ||
    (typeof navigator !== 'undefined' &&
      (navigator.webdriver || /headless|reactsnap|react-snap|prerender/i.test(navigator.userAgent || ''))));

const ensureGtagShim = () => {
  if (!isBrowser()) return null;
  window.dataLayer = window.dataLayer || [];
  if (typeof window.gtag !== 'function') {
    window.gtag = function gtag() {
      window.dataLayer.push(arguments);
    };
  }
  return window.gtag;
};

const isLikelyEEA = () => {
  if (!isBrowser()) return false;
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
    return /^Europe\//.test(tz) || tz === 'Atlantic/Azores' || tz === 'Atlantic/Canary' || tz === 'Atlantic/Madeira';
  } catch {
    return false;
  }
};

export const getStoredConsent = () => {
  if (!isBrowser()) return null;
  try {
    const raw = localStorage.getItem(CONSENT_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const persistConsent = (consent) => {
  if (!isBrowser()) return;
  try {
    localStorage.setItem(
      CONSENT_KEY,
      JSON.stringify({ ...consent, ts: new Date().toISOString() })
    );
  } catch {
    /* ignore */
  }
};

/**
 * Initialise Consent Mode defaults. Must be called before initGA / initGoogleAds.
 * In the EEA we default everything to "denied"; outside we default to "granted"
 * to match expected analytics behavior in the US.
 */
export const initConsent = () => {
  if (isPrerender()) return;
  const gtag = ensureGtagShim();
  if (!gtag) return;

  const stored = getStoredConsent();
  const eea = isLikelyEEA();
  const defaultGranted = stored
    ? stored.ads && stored.analytics
    : !eea;

  gtag('consent', 'default', {
    ad_storage: defaultGranted ? 'granted' : 'denied',
    ad_user_data: defaultGranted ? 'granted' : 'denied',
    ad_personalization: defaultGranted ? 'granted' : 'denied',
    analytics_storage: stored ? (stored.analytics ? 'granted' : 'denied') : (eea ? 'denied' : 'granted'),
    functionality_storage: 'granted',
    security_storage: 'granted',
    wait_for_update: 500,
  });

  if (stored) {
    gtag('consent', 'update', {
      ad_storage: stored.ads ? 'granted' : 'denied',
      ad_user_data: stored.ads ? 'granted' : 'denied',
      ad_personalization: stored.ads ? 'granted' : 'denied',
      analytics_storage: stored.analytics ? 'granted' : 'denied',
    });
  }
};

/** Update consent state and re-emit a Consent Mode update. */
export const setConsent = ({ analytics, ads }) => {
  const gtag = ensureGtagShim();
  if (!gtag) return;
  persistConsent({ analytics: !!analytics, ads: !!ads });
  gtag('consent', 'update', {
    ad_storage: ads ? 'granted' : 'denied',
    ad_user_data: ads ? 'granted' : 'denied',
    ad_personalization: ads ? 'granted' : 'denied',
    analytics_storage: analytics ? 'granted' : 'denied',
  });
};

export const requiresBanner = () => {
  if (!isBrowser() || isPrerender()) return false;
  if (getStoredConsent()) return false;
  return isLikelyEEA();
};
